/* global REMOTE_ORIGIN */

const Events = require('../events.js');
const Fields = require('../fields.js');
const Message = require('../message.js');
const Style = require('../style.js');

/* Constructor */

function Controller(view) {
  var self = this;
  self.view = view;

  self.eventCallbacks = {};  // the functions that are registered on fields to 
                             // be called on events (e.g. on load, error, etc.)

  self.customClasses = {};
  self.customClasses[Fields.CARD_NUMBER] = Style.getDefaultCustomClasses();
  self.customClasses[Fields.CVV] = Style.getDefaultCustomClasses();
  self.customClasses[Fields.EXPIRY] = Style.getDefaultCustomClasses();

  // link message eventListener callback to the view immediately
  self.view.bind('message', function(event) {
    self.receiveMessage(event);
  });

  // bind API methods to use controller 'self' for internal 'this', so that 
  // they behave as expected
  self.create = self.create.bind(self);
  self.on = self.on.bind(self);
  self.createToken = self.createToken.bind(self);
}

Controller.prototype.updateWrapperClass = function(eventName, event) {
  const self = this;
  const field = event.field; 
  const classes = self.customClasses[field];

  if (eventName === Events.focus) {
    self.view.render('addClass', { field: field, class: classes.focus });
  } else if (eventName === Events.blur) {
    self.view.render('removeClass', { field: field, class: classes.focus });
  } else if (eventName === Events.empty) {
    if (event.empty) {
      self.view.render('addClass', { field: field, class: classes.empty });
    } else {
      self.view.render('removeClass', { field: field, class: classes.empty });
    }
  } else if (eventName === Events.complete) {
    if (event.complete) {
      self.view.render('removeClass', { field: field, class: classes.error });
      self.view.render('addClass', { field: field, class: classes.complete });
    } else {
      self.view.render('removeClass', { field: field, class: classes.complete });
    }
  } else if (eventName === Events.error) {
    self.view.render('removeClass', { field: field, class: classes.complete });
    self.view.render('addClass', { field: field, class: classes.error });
  }
};

Controller.prototype.callbackEvent = function(details) {
  const self = this;
  const eventName = details.eventName;
  const event = details.event;

  self.updateWrapperClass(eventName, event);
  if (self.eventCallbacks[eventName]) {
    self.eventCallbacks[eventName](event);
  }
};

Controller.prototype.receiveMessage = function(event) {
  const self = this;
  
  if (!Events.checkOrigin(event, REMOTE_ORIGIN)) {
    return false;
  }

  // Handle message
  let message = event.data;
  if (typeof message === 'string' || message instanceof String) {
    try {
      message = JSON.parse(message);
    } catch (err) {
      self.view.render('error', `Could not parse message: ${err.name} - ${err.message}`);
    }
  }
  const details = message.details;
  
  if (message.name === 'updateIFrame') {
    self.view.render('updateIFrame', details);  
  } else if (message.name === 'callbackEvent') {
    self.callbackEvent(details);
  } else if (message.name === 'tokenResponse') {
    self.eventCallbacks['token'](details.event);
  }
   
  return true;
};

Controller.prototype.setCustomClasses = function(type, classes) {
  const self = this;
  for (let property in classes) {
    if (classes.hasOwnProperty(property)) {
      if (Style.supportedClassProperties.indexOf(property) > -1) {
        if (typeof classes[property] === 'string' || classes[property] instanceof String) {
          self.customClasses[type][property] = classes[property];
        } else {
          self.view.render('error', `'${property}' classes property requires a string value`);
        }
      } else {
        self.view.render('error', `'${property}' is not a supported custom class`);
      }
    }
  }
};

/* API Methods */

Controller.prototype.create = function(type, options={}) {
  const self = this;

  if (type !== Fields.CARD_NUMBER && type !== Fields.CVV && type !== Fields.EXPIRY) {
    self.view.render('error', `type must be ${Fields.CARD_NUMBER}, ${Fields.CVV}, or ${Fields.EXPIRY}`);
    return; 
  }

  if (options.classes) {
    self.setCustomClasses(type, options.classes);
  }
  
  self.view.render('create', type); 
  var msg = Message.create(options);
  self.view.postMessage(msg, type);
  
  return {
    // field object API methods:
    mount: self.mount.bind(this, type),
    unmount: self.unmount.bind(this, type),
    focus: self.focus.bind(this, type),
    blur: self.blur.bind(this, type),
    clear: self.clear.bind(this, type),
    update: self.update.bind(this, type),
  };
};

// Registers the callback in the controller's callbacks object and 
// posts a message to the iframe of the event to respond to. 
Controller.prototype.on = function(eventName, callback) {
  var self = this;

  // Return error if callback type is not supported 
  const supportedEvents = [Events.blur, Events.focus, Events.empty, Events.complete, Events.brand, Events.error];
  if(supportedEvents.indexOf(eventName) <= -1) {
    self.view.render('error', `event type '${eventName}' not supported`);
    return; 
  }

  // register this callback for each of the inputs 
  self.eventCallbacks[eventName] = callback;
};

// Kicks off token creation by registering the callback and 
// sending a 'createToken' message to the iframe.
Controller.prototype.createToken = function(callback) {
  var self = this;
  self.eventCallbacks['token'] = callback;
  self.view.postMessage(Message.beginTokenization(), Fields.CARD_NUMBER);
};

/* field object API methods */

// Method on the object returned from #create()
// type and options arguments are bound to the function on creation
Controller.prototype.mount = function(type, parent) {
  var self = this;

  self.view.render('mount', {
    parent: parent, 
    type: type, 
    errorMsg: `Mount could not find wrapper element '${parent}'`,
  });
  
  self.view.render('addClass', { field: type, class: self.customClasses[type].empty });
  self.view.render('addClass', { field: type, class: self.customClasses[type].base });
};

/*
 * Methods on the object returned from #create()
 * type and options arguments are bound to the function on creation
 */

Controller.prototype.unmount = function(type) {
  var self = this;
  var parent = self.view.getParent(type);
  
  self.view.render('removeClass', { field: type, class: self.customClasses[type].empty });
  self.view.render('removeClass', { field: type, class: self.customClasses[type].base });

  self.view.render('unmount', {
    type: type, 
    errorMsg: `Unmount could not find wrapper element '${parent}'`,
  });
};

Controller.prototype.focus = function(type) {
  self.view.postMessage(Message.focus(), type);
};

Controller.prototype.blur = function(type) {
  self.view.postMessage(Message.blur(), type);
};

Controller.prototype.clear = function(type) {
  self.view.postMessage(Message.clear(), type);
};

Controller.prototype.update = function(type, options={}) {

  if (options.classes) {
    self.setCustomClasses(type, options.classes);
  }
  
  var msg = Message.update(options);
  self.view.postMessage(msg, type);
};

module.exports = Controller;


// WEBPACK FOOTER //
// ./app/main/controller.js